import { processPresetsPolyfill } from './utils'
import type { IPolyfillLogger, PresetPolyfillData } from './types'
import { DATA_PRESET_ATTRIBUTE, MUTATION_OBSERVER_DEBOUNCE_MS, PRESETS_WRAPPER_CLASS } from './constants'

/**
 * DOM mutation observer for builder components polyfill.
 * Watches for child node additions in `wix-presets-wrapper` elements and
 * reapplies the presets polyfill so newly-mounted builder components get
 * their `data-preset` attribute stamped correctly.
 */
export class BuilderComponentMutationObserver {
	private mutationObserver: MutationObserver | null = null
	private debounceTimer: number | null = null
	private target: Element | null = null

	constructor(
		private readonly getPresetsPolyfillData: () => Record<string, PresetPolyfillData>,
		private readonly logger?: IPolyfillLogger
	) {
		this.mutationObserver = new MutationObserver((mutations) => {
			this.handleMutations(mutations)
		})
	}

	private handleMutations(mutations: MutationRecord[]): void {
		this.logger?.interactionStarted('builder-component-mutation-detection')

		try {
			const addedComponentIds: string[] = []
			for (const mutation of mutations) {
				const componentIds = this.getAddedComponentIds(mutation)
				addedComponentIds.push(...componentIds)
			}

			if (addedComponentIds.length > 0) {
				this.scheduleReprocess(addedComponentIds)
			}
		} catch (error) {
			this.logger?.captureError(error as Error, {
				tags: { feature: 'builder-component', method: 'handleMutations' },
			})
		} finally {
			this.logger?.interactionEnded('builder-component-mutation-detection')
		}
	}

	private scheduleReprocess(componentIds: string[]): void {
		if (this.debounceTimer !== null) {
			clearTimeout(this.debounceTimer)
		}

		this.debounceTimer = window.setTimeout(() => {
			this.reprocessPolyfills(componentIds)
			this.debounceTimer = null
		}, MUTATION_OBSERVER_DEBOUNCE_MS)
	}

	private getAddedComponentIds(mutation: MutationRecord): string[] {
		if (!mutation.addedNodes.length) {
			return []
		}

		const componentIds: string[] = []

		for (const node of Array.from(mutation.addedNodes)) {
			if (node instanceof Element && node.classList.contains(PRESETS_WRAPPER_CLASS)) {
				if (node.hasAttribute(DATA_PRESET_ATTRIBUTE)) {
					continue
				}

				for (const className of Array.from(node.classList)) {
					if (className.startsWith('w-')) {
						const componentId = className.substring(2)
						if (componentId) {
							componentIds.push(componentId)
						}
					}
				}
			}
		}

		return componentIds
	}

	private reprocessPolyfills(addedComponentIds: string[]): void {
		if (!this.mutationObserver || !this.target) {
			return
		}

		const shouldMeasure = addedComponentIds.length > 0
		let interactionStarted = false

		try {
			this.mutationObserver.disconnect()

			if (shouldMeasure) {
				this.logger?.interactionStarted('builder-component-mutation-polyfill', {
					customParams: { componentsCount: addedComponentIds.length },
				})
				interactionStarted = true
			}

			const polyfillData = this.getPresetsPolyfillData()
			processPresetsPolyfill(polyfillData)
		} catch (error) {
			this.logger?.captureError(error as Error, {
				tags: { feature: 'builder-component', method: 'reprocessPolyfills' },
			})
		} finally {
			if (interactionStarted) {
				this.logger?.interactionEnded('builder-component-mutation-polyfill', {
					customParams: { componentsCount: addedComponentIds.length },
				})
			}
			this.reconnect()
		}
	}

	private reconnect(): void {
		if (this.mutationObserver && this.target) {
			this.mutationObserver.observe(this.target, { childList: true, subtree: true })
		}
	}

	startObserving(targetElement?: Element): void {
		if (!targetElement && !document.body) {
			return
		}
		this.target = targetElement || document.body
		if (this.mutationObserver) {
			this.mutationObserver.observe(this.target, { childList: true, subtree: true })
		}
	}

	dispose(): void {
		if (this.debounceTimer !== null) {
			clearTimeout(this.debounceTimer)
			this.debounceTimer = null
		}
		if (this.mutationObserver) {
			this.mutationObserver.disconnect()
			this.mutationObserver = null
		}
		this.target = null
	}
}

export const builderComponentPresetsPolyfillMutationObserver = (
	getPresetsPolyfillData: () => Record<string, PresetPolyfillData>,
	logger?: IPolyfillLogger,
	targetElement?: Element
): BuilderComponentMutationObserver => {
	const observer = new BuilderComponentMutationObserver(getPresetsPolyfillData, logger)
	observer.startObserving(targetElement)
	return observer
}
